import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { EntitySummary, Event, EventSummary, NarrativeSummary } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useTextActions } from '@/features/editor/hooks/useTextActions';

export const sceneSchema = z.object({
  name: z.string().min(2, 'Scene name must be at least 2 characters.'),
  description: z.string().optional(),
  eventIds: z.array(z.string()).optional(),
  characterIds: z.array(z.string()).optional(),
  minorCharacters: z.string().optional(),
  organizationIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  settings: z.string().optional(),
  pointOfScene: z.string().optional(),
  povCharacterId: z.string().optional(),
  goal: z.string().optional(),
  conflict: z.string().optional(),
  themes: z.string().optional(),
  changesToMake: z.string().optional(),
});

export type SceneSchemaType = z.infer<typeof sceneSchema>;

export function useCreateSceneForm() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/scenes/new' });
  const chapterId = search.chapterId || '';
  const {
    novel,
    firestore,
    saveItem,
    getNewId,
    updateNarrativeSummary,
  } = useNovelRepository();
  const { createNewText } = useTextActions();
  const novelId = novel.id;
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [selectedEventForPopup, setSelectedEventForPopup] = useState<Event | EventSummary | null>(null);

  const formMethods = useForm<SceneSchemaType>({
    resolver: zodResolver(sceneSchema),
    defaultValues: {
      name: '',
      description: '',
      eventIds: [],
      characterIds: [],
      minorCharacters: '',
      organizationIds: [],
      locationIds: [],
      settings: '',
      pointOfScene: '',
      povCharacterId: '',
      goal: '',
      conflict: '',
      themes: '',
      changesToMake: '',
    },
  });

  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = formMethods;

  const characters = Object.values(novel.summaries.characters);
  const locations = Object.values(novel.summaries.locations);
  const organizations = Object.values(novel.summaries.organizations);
  const events = Object.values(novel.summaries.events).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const scenes = Object.values(novel.summaries.scenes);

  const isLoadingCharacters = false;
  const isLoadingLocations = false;
  const isLoadingOrganizations = false;
  const isLoadingEvents = false;

  const selectedEventIds = watch('eventIds') || [];
  const selectedCharacterIds = watch('characterIds') || [];
  const selectedOrganizationIds = watch('organizationIds') || [];

  const prevEventIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!events || isLoadingEvents) return;

    const addedIds = selectedEventIds.filter(id => !prevEventIdsRef.current.includes(id));

    if (addedIds.length > 0) {
      const currentCharaIds = getValues('characterIds') || [];
      const currentOrgIds = getValues('organizationIds') || [];
      const currentLocIds = getValues('locationIds') || [];

      const newCharacterIds = new Set(currentCharaIds);
      const newOrgIds = new Set(currentOrgIds);
      const newLocIds = new Set(currentLocIds);

      let changed = false;

      addedIds.forEach(eventId => {
        const event = events.find(e => e.id === eventId);
        if (event) {
          if (event.characterIds) {
            event.characterIds.forEach((id: string) => {
              if (!newCharacterIds.has(id)) {
                newCharacterIds.add(id);
                changed = true;
              }
            });
          }
          if (event.organizationIds) {
            event.organizationIds.forEach((id: string) => {
              if (!newOrgIds.has(id)) {
                newOrgIds.add(id);
                changed = true;
              }
            });
          }
          if (event.locationIds) {
            event.locationIds.forEach((id: string) => {
              if (!newLocIds.has(id)) {
                newLocIds.add(id);
                changed = true;
              }
            });
          }
        }
      });

      if (changed) {
        setValue('characterIds', Array.from(newCharacterIds));
        setValue('organizationIds', Array.from(newOrgIds));
        setValue('locationIds', Array.from(newLocIds));
      }
    }

    prevEventIdsRef.current = selectedEventIds;
  }, [events, isLoadingEvents, selectedEventIds, getValues, setValue]);

  useEffect(() => {
    if (!novelId) {
      navigate({ to: '/scenes' });
    }
  }, [novelId, navigate]);

  async function onSubmit(values: SceneSchemaType) {
    if (!firestore || !novelId) {
      toast({
        title: t('entity_form.scenes.error_title'),
        description: t('entity_form.scenes.error_no_novel'),
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Calculate next order in chapter
      const chapterScenes = scenes.filter(s => (s.chapterId || '') === chapterId);
      const nextOrder = chapterScenes.reduce((max, s) => Math.max(max, s.order ?? 0), -1) + 1;

      const sceneId = getNewId ? getNewId() : '';

      // 1. Create content document immediately
      const contentId = await createNewText(values.name, [sceneId]);

      const finalData = {
        ...values,
        id: sceneId,
        chapterId,
        order: nextOrder,
        mainContentId: contentId || undefined,
        shortDescription: values.description ? values.description.substring(0, 150) : '',
      };

      await saveItem('narrative', 'scenes', finalData);

      // Update summary
      await updateNarrativeSummary('scenes', sceneId, {
        id: sceneId,
        name: values.name,
        updatedAt: new Date().toISOString(),
        status: { phase: 'idle', attempts: 0 },
        chapterId,
        order: nextOrder,
        mainContentId: contentId || undefined,
        shortDescription: finalData.shortDescription,
        povCharacterId: values.povCharacterId || '',
        locationIds: values.locationIds || [],
        eventIds: values.eventIds || [],
        characterIds: values.characterIds || [],
        organizationIds: values.organizationIds || [],
      } as NarrativeSummary);

      toast({
        title: t('entity_form.scenes.create_success_title'),
        description: t('entity_form.scenes.create_success_desc', { name: values.name }),
      });
      navigate({ to: '/scenes' });
    } catch (error) {
      console.error('Error creating scene:', error);
      toast({
        title: t('entity_form.scenes.error_title'),
        description: t('entity_form.scenes.create_failed_desc'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  const selectedEvents = selectedEventIds
    .map((id: string) => events?.find((e) => e.id === id))
    .filter((e): e is EventSummary => !!e);

  const selectedCharacterNames = selectedCharacterIds
    .map((id: string) => characters?.find((c) => c.id === id)?.name)
    .filter((name): name is string => !!name);

  const selectedOrganizationNames = selectedOrganizationIds
    .map((id: string) => organizations?.find((o) => o.id === id)?.name)
    .filter((name): name is string => !!name);

  return {
    register,
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSaving,
    characters,
    locations,
    organizations,
    events,
    isLoadingCharacters,
    isLoadingLocations,
    isLoadingOrganizations,
    isLoadingEvents,
    selectedEvents,
    selectedCharacterNames,
    selectedOrganizationNames,
    selectedEventForPopup,
    setSelectedEventForPopup,
    novelId,
    t,
  };
}
